/** Contratos da API de Hora Extra (`/api/rh/hora-extra/*`). */

export type HoraExtraStatus = 'pendente' | 'aprovado' | 'reprovado' | 'parcial';

export const HORA_EXTRA_STATUS_BADGE: Record<HoraExtraStatus, string> = {
    pendente: 'bg-warning/15 text-warning',
    aprovado: 'bg-success/15 text-success',
    reprovado: 'bg-danger/15 text-danger',
    parcial: 'bg-info/15 text-info',
};

/**
 * Permissões que dão acesso à tela de Hora Extra — qualquer uma basta.
 *
 * Constante única porque o mesmo conjunto guarda três portas: o card do
 * launcher, o card do hub do RH e a rota. Quando os níveis de Gestor e Diretor
 * nasceram, as três listas ficaram para trás em silêncio: as contas logavam,
 * viam "0 sistemas" e a rota barrava.
 *
 * A lista única sincroniza as três portas, mas não lembra de incluir a
 * permissão nova — `rh.hora_extra.aprovar.etapa` ficou de fora quando o motor
 * configurável nasceu. Quem trava isso é `horaExtraPortas.test.ts` (igualdade
 * exata) somado a `horaExtraRotaProtegida.test.tsx`, que exercita cada
 * permissão sozinha contra o `ProtectedRoute` real.
 */
export const HORA_EXTRA_PERMISSOES = [
    'rh.hora_extra',
    'rh.hora_extra.aprovar.gestor',
    'rh.hora_extra.aprovar.diretor',
    'rh.hora_extra.aprovar.etapa',
    'rh.hora_extra.aprovar',
    'rh.hora_extra.parametros',
] as const;

/** O que o backend diz que este usuário pode fazer. Nunca reconstruir no cliente. */
export type HoraExtraContexto = {
    pode_solicitar: boolean;
    motivo: string | null;
    cargo: string | null;
    pode_aprovar_etapa: boolean;
    pode_aprovar_gestor: boolean;
    pode_aprovar_diretor: boolean;
    pode_aprovar: boolean;
    pode_editar_parametros: boolean;
};

/** Colaborador elegível — sem salário: o valor vem calculado do `/preview`. */
export type ColaboradorElegivel = {
    id: number;
    registro: string;
    nome: string;
    empresa: string;
    cargo: string | null;
    setor_descricao: string | null;
    setor_codigo: string | null;
    setor_nivel_anterior_descricao: string | null;
    organograma: string | null;
    admissao: string | null;
    situacao: string | null;
};

/** Locais pelos quais o solicitante responde. Vem de `rh_local_responsaveis`. */
export type LocalDoSolicitante = { id: number; nome: string; tipo: string };

export type HoraExtraFiltrosBase = {
    locais: LocalDoSolicitante[];
};

export type HoraExtraJornada = {
    dia: string;
    hora_inicio: string;
    hora_fim: string;
    intervalo: number;
};

export type HoraExtraPreviewPayload = HoraExtraJornada & {
    colaborador_ids: number[];
};

export type HoraExtraStorePayload = HoraExtraPreviewPayload & {
    justificativa: string;
    obra: string | null;
};

export type HoraExtraParametros = {
    adicional_semana: number;
    adicional_fim_semana: number;
    divisor_horas: number;
    divisor_dsr: number;
    feriados_extras: string[];
};

/** Item do preview: dados de exibição + valores já calculados pelo servidor. */
export type PreviewItem = {
    colaborador_id: number;
    nome: string;
    registro: string;
    empresa: string;
    cargo: string | null;
    setor_descricao: string | null;
    setor_codigo: string | null;
    setor_nivel_anterior_descricao: string | null;
    organograma: string | null;
    admissao: string | null;
    situacao: string | null;
    horas: number;
    valor_he: number;
    valor_dsr: number;
    custo_total: number;
};

export type HoraExtraTotais = {
    colaboradores: number;
    horas: number;
    valor_he: number;
    valor_dsr: number;
    custo_total: number;
};

export type HoraExtraPreview = {
    itens: PreviewItem[];
    totais: HoraExtraTotais;
    parametros: HoraExtraParametros & {
        adicional: number;
        feriado: string | null;
        fim_de_semana: boolean;
    };
};

/**
 * Recorte público do snapshot congelado.
 *
 * O snapshot no banco guarda o salário (é o que permite recalcular o item anos
 * depois), mas os Resources aplicam whitelist — `salario` e `salario_snapshot`
 * nunca chegam ao cliente. Ver `HoraExtraItemResource::CAMPOS_PUBLICOS`.
 */
export type ColaboradorSnapshot = {
    nome: string;
    cargo: string | null;
    empresa: string;
    admissao: string | null;
    situacao: string | null;
    organograma: string | null;
    setor_codigo: string | null;
    setor_descricao: string | null;
    setor_nivel_anterior_descricao: string | null;
};

/** Decisão de um nível. `status` nulo = o nível ainda não foi alcançado. */
export type DecisaoDoNivel = {
    status: string | null;
    motivo: string | null;
    por: string | null;
    em: string | null;
};

/** Uma etapa da esteira configurável, como o solicitante a vê. */
export type EtapaDoItem = {
    ordem: number;
    label: string;
    aprovador: string | null;
    status: 'pendente' | 'aprovada' | 'reprovada';
    decidido_por: string | null;
    decidido_em: string | null;
    motivo: string | null;
};

export type HoraExtraItem = {
    id: number;
    colaborador_id: number | null;
    colaborador: ColaboradorSnapshot;
    horas: number;
    valor_he: number;
    valor_dsr: number;
    custo_total: number;
    status: Exclude<HoraExtraStatus, 'parcial'>;
    status_label: string;
    motivo: string | null;
    /**
     * As três decisões do fluxo anterior. **Nulo** no item da esteira por
     * unidade, onde quem conta a história é `etapas`.
     */
    decisoes?: Record<'gestor' | 'diretor' | 'dp', DecisaoDoNivel> | null;
    /** A esteira configurada da unidade. Nulo no item do fluxo anterior. */
    etapas?: EtapaDoItem[] | null;
    decidido_por?: { id: number; name: string };
    decidido_em: string | null;
};

export type HoraExtraSolicitacao = {
    id: number;
    dia: string;
    hora_inicio: string;
    hora_fim: string;
    intervalo: number;
    justificativa: string;
    obra: string | null;
    status: HoraExtraStatus;
    status_label: string;
    parametros: HoraExtraParametros;
    totais: HoraExtraTotais;
    itens?: HoraExtraItem[];
    solicitante?: { id: number; name: string };
    created_at: string;
};

/** Linha da fila do DP: item achatado com o contexto do lote. */
export type FilaItem = {
    id: number;
    solicitacao_id: number;
    colaborador: ColaboradorSnapshot;
    dia: string;
    hora_inicio: string;
    hora_fim: string;
    intervalo: number;
    justificativa: string;
    obra: string | null;
    gestor: string | null;
    /** Quando o lote foi enviado ao DP — não confundir com `dia`, a data da hora extra. */
    solicitado_em: string | null;
    horas: number;
    valor_he: number;
    valor_dsr: number;
    custo_total: number;
    status: Exclude<HoraExtraStatus, 'parcial'>;
    status_label: string;
    motivo: string | null;
    decidido_por: string | null;
    decidido_em: string | null;
};

export type FilaResumo = {
    pendentes: number;
    aprovados: number;
    reprovados: number;
    horas_solicitadas: number;
    horas_aprovadas: number;
    custo_solicitado: number;
    custo_aprovado: number;
    custo_pendente: number;
};

export type FilaFiltros = {
    status?: string;
    empresa?: string;
    setor?: string;
    q?: string;
    page?: number;
    per_page?: number;
};

/** Item na fila da primeira aprovação. Sem o salário bruto: só o custo calculado. */
export type ItemDoGestor = {
    id: number;
    solicitacao_id: number;
    colaborador: ColaboradorSnapshot;
    /** Local que decide quem aprova. Vem de `rh_local_responsaveis`, não da lotação. */
    local: string | null;
    dia: string;
    hora_inicio: string;
    hora_fim: string;
    intervalo: number;
    justificativa: string;
    solicitante: string | null;
    solicitado_em: string | null;
    horas: number;
    valor_he: number;
    valor_dsr: number;
    custo_total: number;
    gestor_status: string | null;
    gestor_motivo: string | null;
    gestor_em: string | null;
    status: HoraExtraStatus;
    status_label: string;
    /** Qual nível falta decidir: `gestor`, `diretor`, `dp` ou nulo se encerrado. */
    nivel_pendente: 'gestor' | 'diretor' | 'dp' | null;
    /** Informativo: o de-para de cargos confere, mas não escolhe a pessoa. */
    hierarquia_coerente: boolean | null;
};

/**
 * Item na fila da segunda aprovacao.
 *
 * Sem `local` para recorte: o Diretor responde pelo fluxo inteiro. O local
 * continua vindo como contexto, e `gestor_por` mostra quem ja aprovou.
 */
export type ItemDoDiretor = {
    id: number;
    solicitacao_id: number;
    colaborador: ColaboradorSnapshot;
    local: string | null;
    dia: string;
    hora_inicio: string;
    hora_fim: string;
    intervalo: number;
    justificativa: string;
    solicitante: string | null;
    solicitado_em: string | null;
    horas: number;
    valor_he: number;
    valor_dsr: number;
    custo_total: number;
    gestor_por: string | null;
    gestor_em: string | null;
    diretor_status: string | null;
    diretor_motivo: string | null;
    diretor_em: string | null;
    status: HoraExtraStatus;
    status_label: string;
    nivel_pendente: 'gestor' | 'diretor' | 'dp' | null;
};

/**
 * Item na fila da esteira configurável por unidade.
 *
 * Uma fila só para todos os aprovadores: o que limita é a **etapa corrente**.
 * `etapa_label` é o rótulo que o RH escreveu ("Engenheiro da obra", "DP") — não
 * há nome de papel fixo neste fluxo.
 */
export type ItemDaMinhaFila = {
    id: number;
    solicitacao_id: number;
    colaborador: ColaboradorSnapshot;
    local: string | null;
    unidade: string | null;
    dia: string;
    hora_inicio: string;
    hora_fim: string;
    intervalo: number;
    justificativa: string;
    solicitante: string | null;
    solicitado_em: string | null;
    horas: number;
    valor_he: number;
    valor_dsr: number;
    custo_total: number;
    /** Índice da etapa corrente. Com `total_etapas`, vira "3 de 5". */
    etapa_atual: number;
    etapa_label: string | null;
    total_etapas: number;
    minha_vez: boolean;
    etapas: EtapaDoItem[];
    status: HoraExtraStatus;
    status_label: string;
};

export type FeriadoNacional = { ano: number; data: string; nome: string };

export type Paginacao = {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
};
